import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

/**
 * DesignReferencesCard
 * -------------------------------------------------------------------------
 * First sub-card of the Design Analysis tab. Acquires the reference inputs the
 * analysis reads:
 *   - FLOOR PLANS   Ground by default; upper floors + a Typical option added
 *                   on demand from the generated massing.
 *   - ELEVATIONS    Four fixed slots: North, South, East, West (facade
 *                   drawings — NOT terrain, which is utils/elevation.js).
 *   - 3D MODEL      One file, glTF/GLB primary, OBJ fallback.
 *
 * Floor plans + elevations accept PDF, JPG, PNG. The model accepts GLB/GLTF/OBJ.
 * This card only handles acquisition + local preview + persistence of the refs;
 * parsing / room-extraction is a later step.
 *
 * DESIGN-STAGE / SCREENING framing applies (as throughout VAUOM): uploads are
 * advisory inputs an architect confirms, not authoritative geometry.
 *
 * NOTE on PDFs: the CV/OCR/Gemini pipeline expects raster input, so a PDF that
 * feeds per-room extraction needs a rasterize-first step (page 1, or a page
 * picker). Accepted here as a reference regardless; flagged for the parse path.
 *
 * INTEGRATION SEAMS (TODO(wire)) — reconcile against the real files:
 *   1. uploadReference(): point at the image-persistence route behind
 *      ImageAnalysisPanel / services/imageAnalysisService.js (SHA-256 store)
 *      that returns { refId, url } for a saved file.
 *   2. persistRefs(): mirror CostAnalysisPanel's persistProjectCache — PUT
 *      /api/projects/:id partial-merge — to save `designReferences`.
 *   3. API_BASE: import the centralised constant instead of the "" prop default.
 * ------------------------------------------------------------------------- */

// ---- tokens (fallbacks mirror TemperaturePanel) ---------------------------
const T = {
  surface: "var(--surface-2,#1a2234)",
  surfaceHi: "var(--surface-3,#212a3e)",
  border: "var(--border,#3a4456)",
  borderHi: "var(--border-hi,#4a566e)",
  text: "var(--text,#e6ebf5)",
  textDim: "var(--text-dim,#9aa7c0)",
  accent: "var(--accent,#5b9dff)",
  ok: "var(--ok,#3ecf8e)",
  warn: "var(--warn,#f0b429)",
  err: "var(--err,#f0616d)",
};

const ACCEPT_DOC = ".pdf,.jpg,.jpeg,.png";   // floor plans + elevations
const ACCEPT_MODEL = ".glb,.gltf,.obj";      // 3D model
const IMG_EXT = ["png", "jpg", "jpeg", "webp"];
const MAX_DOC_MB = 20;
const MAX_MODEL_MB = 60;
export const TYPICAL_ID = "typical";

const ELEVATIONS = [
  { key: "N", label: "North Elevation" },
  { key: "S", label: "South Elevation" },
  { key: "E", label: "East Elevation" },
  { key: "W", label: "West Elevation" },
];

// ---- floor labelling ------------------------------------------------------
const ORDINALS = [
  "Ground", "First", "Second", "Third", "Fourth", "Fifth", "Sixth",
  "Seventh", "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth",
];
function ordinal(n) {
  if (n < ORDINALS.length) return ORDINALS[n];
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
export function floorLabel(index) {
  return index === 0 ? "Ground Floor Plan" : `${ordinal(index)} Floor Plan`;
}

function extOf(name) {
  return (name.split(".").pop() || "").toLowerCase();
}
function isImageName(name) {
  return IMG_EXT.includes(extOf(name));
}
function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
function emptyEntry(label) {
  return { label, file: null, previewUrl: null, uploading: false, error: null, refId: null };
}

// ---- one upload slot ------------------------------------------------------
function UploadSlot({ label, accept, hint, entry, removable, onFile, onRemove }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const pick = () => inputRef.current && inputRef.current.click();
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  const filled = !!entry && !!entry.file;
  const ext = filled ? extOf(entry.file.name).toUpperCase() : "";
  const showImg = filled && isImageName(entry.file.name) && entry.previewUrl;

  return (
    <div style={styles.slot}>
      <div style={styles.slotHead}>
        <span style={styles.slotLabel}>{label}</span>
        {removable && (
          <button style={styles.linkBtn} onClick={onRemove} title="Remove">Remove</button>
        )}
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={pick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pick()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        style={{
          ...styles.drop,
          border: `1px solid ${drag ? T.accent : filled ? T.borderHi : T.border}`,
          background: drag ? T.surfaceHi : T.surface,
        }}
      >
        {filled ? (
          <div style={styles.filledRow}>
            {showImg ? (
              <img src={entry.previewUrl} alt="" style={styles.thumb} />
            ) : (
              <div style={styles.thumbFallback}>{ext || "FILE"}</div>
            )}
            <div style={styles.fileMeta}>
              <div style={styles.fileName} title={entry.file.name}>{entry.file.name}</div>
              <div style={styles.fileSub}>
                {fmtSize(entry.file.size)}
                {entry.uploading && <span style={{ color: T.textDim }}> · uploading…</span>}
                {entry.error && <span style={{ color: T.err }}> · {entry.error}</span>}
                {entry.refId && !entry.uploading && !entry.error && (
                  <span style={{ color: T.ok }}> · saved</span>
                )}
              </div>
            </div>
            <button style={styles.linkBtn} onClick={(e) => { e.stopPropagation(); pick(); }}>
              Replace
            </button>
          </div>
        ) : (
          <div style={styles.emptyRow}>
            <span style={styles.emptyMain}>Drop a file or click to upload</span>
            {hint && <span style={styles.emptyHint}>{hint}</span>}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ---- main card ------------------------------------------------------------
export default function DesignReferencesCard({
  project,
  apiBase = "",            // TODO(wire): centralised API_BASE
  initialFloors = 1,
  onChange,                // (designReferences) => void
}) {
  const projectId = project && project.id;

  const [floors, setFloors] = useState(
    (project && project.cachedMassingFloors) || initialFloors
  );
  const [plans, setPlans] = useState(() => ({ "0": emptyEntry(floorLabel(0)) }));
  const [elevations, setElevations] = useState(() => {
    const o = {};
    ELEVATIONS.forEach((e) => { o[e.key] = emptyEntry(e.label); });
    return o;
  });
  const [model, setModel] = useState(() => emptyEntry("Building model"));
  const [addSel, setAddSel] = useState("");
  const [dirty, setDirty] = useState(0);

  // keep a live mirror so persistence reads the latest of every slice
  const stateRef = useRef({ plans, elevations, model });
  useEffect(() => { stateRef.current = { plans, elevations, model }; }, [plans, elevations, model]);
  const markDirty = () => setDirty((d) => d + 1);

  // Model visibility in the 3D view. Site3DView owns the actual scene object;
  // this only asks it to toggle and reflects what it reports back, so the two
  // can't drift apart if visibility is changed from the toolbar instead.
  const [modelVisible, setModelVisible] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [modelLoadState, setModelLoadState] = useState({ loading: false, error: null });
  useEffect(() => {
    const onState = (e) => {
      setModelVisible(!!e.detail?.visible);
      setModelReady(!!e.detail?.loaded);
      setModelLoadState({ loading: !!e.detail?.loading, error: e.detail?.error || null });
    };
    window.addEventListener('designModelState', onState);
    return () => window.removeEventListener('designModelState', onState);
  }, []);

  // The store returns a path (/api/design-reference/<sha>); the backend is a
  // different origin in Codespaces, so make it absolute before using it as a
  // preview src. Rebuilt from refId on hydrate so previews survive a reload —
  // the blob: URLs used during upload do not.
  const refUrl = useCallback(
    (refIdOrUrl) => {
      if (!refIdOrUrl) return null;
      if (/^(blob:|https?:)/.test(refIdOrUrl)) return refIdOrUrl;
      const pathPart = refIdOrUrl.startsWith('/')
        ? refIdOrUrl
        : `/api/design-reference/${refIdOrUrl}`;
      return `${apiBase}${pathPart}`;
    },
    [apiBase]
  );

  // ---- hydrate from persisted project.designReferences --------------------
  const hydrate = useCallback((dr) => {
    if (!dr) return;
    if (dr.floorPlans && dr.floorPlans.length) {
      const next = {};
      dr.floorPlans.forEach((fp) => {
        next[fp.floorId] = {
          ...emptyEntry(fp.label),
          file: fp.fileName ? { name: fp.fileName, size: fp.size || 0 } : null,
          previewUrl: refUrl(fp.url || fp.refId),
          refId: fp.refId || null,
        };
      });
      if (!next["0"]) next["0"] = emptyEntry(floorLabel(0));
      setPlans(next);
    }
    if (dr.elevations && dr.elevations.length) {
      setElevations((prev) => {
        const next = { ...prev };
        dr.elevations.forEach((ev) => {
          if (next[ev.dir]) {
            next[ev.dir] = {
              ...next[ev.dir],
              file: ev.fileName ? { name: ev.fileName, size: ev.size || 0 } : null,
              previewUrl: refUrl(ev.url || ev.refId),
              refId: ev.refId || null,
            };
          }
        });
        return next;
      });
    }
    if (dr.model3d) {
      setModel({
        ...emptyEntry("Building model"),
        file: dr.model3d.fileName ? { name: dr.model3d.fileName, size: dr.model3d.size || 0 } : null,
        refId: dr.model3d.refId || null,
      });
    }
  }, [refUrl]);

  useEffect(() => { hydrate(project && project.designReferences); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rehydrate when a drawing is added to this project from ELSEWHERE — the intake
  // panels on the main pane write project.designReferences directly. Without this
  // the card kept its old slots while open, and its next save would have written
  // them back over the new drawing. Its own saves dispatch the same event with no
  // designReferences payload, so they are not mistaken for an outside change.
  useEffect(() => {
    const onChanged = (e) => {
      const d = (e && e.detail) || {};
      if (!d.designReferences || String(d.projectId) !== String(projectId)) return;
      hydrate(d.designReferences);
    };
    window.addEventListener("designReferencesChanged", onChanged);
    return () => window.removeEventListener("designReferencesChanged", onChanged);
  }, [projectId, hydrate]);

  // ---- massing → floor count ----------------------------------------------
  useEffect(() => {
    const onMassing = (e) => {
      const n = e && e.detail && Number(e.detail.floors);
      if (Number.isFinite(n) && n > 0) setFloors(n);
    };
    window.addEventListener("massingBuilt", onMassing);
    return () => window.removeEventListener("massingBuilt", onMassing);
  }, []);

  // ---- revoke blob URLs on unmount ----------------------------------------
  useEffect(() => {
    return () => {
      const { plans, elevations, model } = stateRef.current;
      [...Object.values(plans), ...Object.values(elevations), model].forEach(
        (e) => e && e.previewUrl && e.previewUrl.startsWith("blob:") && URL.revokeObjectURL(e.previewUrl)
      );
    };
  }, []);

  // ---- build + persist ----------------------------------------------------
  const buildRefs = useCallback(() => {
    const { plans, elevations, model } = stateRef.current;
    const floorPlans = Object.entries(plans)
      .filter(([, v]) => v && v.file)
      .map(([floorId, v]) => ({
        floorId, label: v.label, fileName: v.file.name, size: v.file.size, refId: v.refId || null,
      }));
    const elevs = Object.entries(elevations)
      .filter(([, v]) => v && v.file)
      .map(([dir, v]) => ({
        dir, label: v.label, fileName: v.file.name, size: v.file.size, refId: v.refId || null,
      }));
    const model3d = model && model.file
      ? { fileName: model.file.name, size: model.file.size, refId: model.refId || null }
      : null;
    return { floorPlans, elevations: elevs, model3d };
  }, []);

  // TODO(wire): mirror persistProjectCache — PUT /api/projects/:id partial merge.
  const persistRefs = useCallback((refs) => {
    if (!projectId) return;
    fetch(`${apiBase}/api/projects/${projectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ designReferences: refs }),
    }).catch(() => { /* non-fatal; refs still held locally */ });
  }, [apiBase, projectId]);

  // fires only on completed changes; stateRef is synced first (effect order)
  useEffect(() => {
    if (dirty === 0) return;
    const refs = buildRefs();
    if (onChange) onChange(refs);
    persistRefs(refs);
    // Tell the rest of the app the reference set moved.
    //
    // This card and the Design Analysis drawer are visible AT THE SAME TIME —
    // the card on the right, the drawer along the bottom. PlanParseView read the
    // reference list once when it mounted, so a plan uploaded while it was open
    // left it sitting on "No floor plan uploaded yet" next to a card saying
    // "saved". Every mutation here funnels through markDirty(), so this one
    // dispatch covers upload, replace and remove alike. Emitted AFTER the upload
    // has been awaited, so a listener that re-fetches finds the ref already
    // stored rather than racing it.
    window.dispatchEvent(new CustomEvent('designReferencesChanged',
      { detail: { projectId: projectId ?? null } }));
  }, [dirty]);

  // ---- upload -------------------------------------------------------------
  // TODO(wire): point at the real image-persistence endpoint.
  const uploadReference = useCallback(async (file, meta) => {
    const form = new FormData();
    form.append("file", file);
    if (projectId) form.append("projectId", projectId);
    form.append("kind", meta.kind);           // "floorPlan" | "elevation" | "model3d"
    if (meta.slot != null) form.append("slot", meta.slot);
    const res = await fetch(`${apiBase}/api/design-reference`, { method: "POST", body: form });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    return res.json();                        // expect { refId, url? }
  }, [apiBase, projectId]);

  const validate = (file, isModel) => {
    const maxMb = isModel ? MAX_MODEL_MB : MAX_DOC_MB;
    if (file.size > maxMb * 1024 * 1024) return `Too large (max ${maxMb} MB)`;
    const accept = (isModel ? ACCEPT_MODEL : ACCEPT_DOC).split(",");
    if (!accept.includes("." + extOf(file.name))) return `Use ${accept.join(", ")}`;
    return null;
  };

  // one generic flow for floor plans + elevations (both are docs)
  const handleDocFile = async (setSlice, key, file, kind) => {
    const error = validate(file, false);
    const preview = isImageName(file.name) ? URL.createObjectURL(file) : null;
    setSlice((prev) => {
      const old = prev[key];
      if (old && old.previewUrl && old.previewUrl.startsWith("blob:")) URL.revokeObjectURL(old.previewUrl);
      return { ...prev, [key]: { ...old, file, previewUrl: error ? null : preview, uploading: !error, error, refId: null } };
    });
    if (error) { if (preview) URL.revokeObjectURL(preview); return; }
    try {
      const { refId, url } = await uploadReference(file, { kind, slot: key });
      setSlice((prev) => ({ ...prev, [key]: { ...prev[key], uploading: false, refId, previewUrl: refUrl(url) || prev[key].previewUrl } }));
      markDirty();
    } catch (err) {
      setSlice((prev) => ({ ...prev, [key]: { ...prev[key], uploading: false, error: err.message } }));
    }
  };

  const handleModelFile = async (file) => {
    const error = validate(file, true);
    setModel((m) => ({ ...m, file, previewUrl: null, uploading: !error, error, refId: null }));
    if (error) return;
    try {
      const { refId } = await uploadReference(file, { kind: "model3d" });
      setModel((m) => ({ ...m, uploading: false, refId }));
      markDirty();
    } catch (err) {
      setModel((m) => ({ ...m, uploading: false, error: err.message }));
    }
  };

  // ---- floor add / remove -------------------------------------------------
  const addOptions = useMemo(() => {
    const opts = [];
    for (let i = 1; i < floors; i++) {
      if (!plans[String(i)]) opts.push({ id: String(i), label: floorLabel(i) });
    }
    if (!plans[TYPICAL_ID] && floors > 1) {
      opts.push({ id: TYPICAL_ID, label: "Typical Floor Plan (upper floors)" });
    }
    return opts;
  }, [floors, plans]);

  const addFloor = (id) => {
    if (!id) return;
    const label = id === TYPICAL_ID ? "Typical Floor Plan" : floorLabel(Number(id));
    setPlans((prev) => ({ ...prev, [id]: emptyEntry(label) }));
    setAddSel("");
  };

  const removeSlot = (setSlice, key, hadFile) => {
    setSlice((prev) => {
      const p = prev[key];
      if (p && p.previewUrl && p.previewUrl.startsWith("blob:")) URL.revokeObjectURL(p.previewUrl);
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (hadFile) markDirty();
  };

  const clearElevation = (key) => {
    setElevations((prev) => {
      const p = prev[key];
      if (p && p.previewUrl && p.previewUrl.startsWith("blob:")) URL.revokeObjectURL(p.previewUrl);
      return { ...prev, [key]: emptyEntry(p.label) };
    });
    markDirty();
  };

  const clearModel = () => {
    setModel((m) => {
      if (m.previewUrl && m.previewUrl.startsWith("blob:")) URL.revokeObjectURL(m.previewUrl);
      return emptyEntry("Building model");
    });
    markDirty();
  };

  const orderedPlanIds = useMemo(() => {
    return Object.keys(plans).sort((a, b) => {
      if (a === TYPICAL_ID) return 1;
      if (b === TYPICAL_ID) return -1;
      return Number(a) - Number(b);
    });
  }, [plans]);

  const massingReady = floors > 1;
  const docHint = "PDF · JPG · PNG";

  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <span style={styles.cardTitle}>Reference uploads</span>
        <span style={styles.cardMeta}>
          {massingReady ? `${floors} floors in massing` : "massing not built"}
        </span>
      </div>

      {/* Floor plans */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Floor plans</div>
        {orderedPlanIds.map((id) => (
          <UploadSlot
            key={id}
            label={plans[id].label}
            accept={ACCEPT_DOC}
            hint={docHint}
            entry={plans[id]}
            removable={id !== "0"}
            onFile={(f) => handleDocFile(setPlans, id, f, "floorPlan")}
            onRemove={() => removeSlot(setPlans, id, !!plans[id].file)}
          />
        ))}
        <div style={styles.addRow}>
          <select
            value={addSel}
            disabled={!massingReady || addOptions.length === 0}
            onChange={(e) => addFloor(e.target.value)}
            style={{ ...styles.select, opacity: !massingReady || addOptions.length === 0 ? 0.5 : 1 }}
          >
            <option value="">
              {!massingReady
                ? "Build massing to add upper floors"
                : addOptions.length === 0
                ? "All floors added"
                : "Add another floor plan…"}
            </option>
            {addOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Elevations */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Elevations</div>
        {ELEVATIONS.map((ev) => (
          <UploadSlot
            key={ev.key}
            label={ev.label}
            accept={ACCEPT_DOC}
            hint={docHint}
            entry={elevations[ev.key]}
            removable={!!elevations[ev.key].file}
            onFile={(f) => handleDocFile(setElevations, ev.key, f, "elevation")}
            onRemove={() => clearElevation(ev.key)}
          />
        ))}
      </div>

      {/* 3D model */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>3D model</div>
        <UploadSlot
          label="Building model"
          accept={ACCEPT_MODEL}
          hint="GLB / glTF preferred · OBJ supported"
          entry={model}
          removable={false}
          onFile={handleModelFile}
          onRemove={clearModel}
        />
        {model.file && modelLoadState.error && (
          <div style={{
            marginTop: 8, fontSize: 11, lineHeight: 1.5,
            color: 'var(--err,#d9534f)',
            border: '1px solid var(--err,#d9534f)', borderRadius: 7, padding: '6px 9px',
          }}>
            The 3D view could not load this model — {modelLoadState.error}.
          </div>
        )}
        {model.file && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              disabled={!modelReady}
              onClick={() => window.dispatchEvent(new CustomEvent('toggleDesignModelRequested'))}
              title={modelReady
                ? (modelVisible ? 'Hide the model in the 3D view' : 'Show the model in the 3D view')
                : modelLoadState.error
                ? `Could not load: ${modelLoadState.error}`
                : modelLoadState.loading
                ? 'Loading the model into the 3D view…'
                : 'The 3D view has not loaded this model'}
              style={{
                fontSize: 11.5, fontWeight: 600, padding: '5px 11px', borderRadius: 7,
                cursor: modelReady ? 'pointer' : 'not-allowed',
                border: `1px solid ${modelVisible ? 'var(--ok,#3ecf8e)' : 'var(--border,#3a4456)'}`,
                background: modelVisible ? 'var(--ok,#3ecf8e)' : 'transparent',
                color: modelVisible ? '#01212a' : 'var(--text-secondary,#c3c9d4)',
                opacity: modelReady ? 1 : 0.5,
              }}
            >
              {modelLoadState.error
                ? '⚠ Failed to load'
                : !modelReady && modelLoadState.loading
                ? '… Loading'
                : modelVisible ? '● Visible in 3D' : '○ Hidden in 3D'}
            </button>
            <button style={styles.linkBtn} onClick={clearModel}>Clear model</button>
          </div>
        )}
        <div style={styles.note}>
          GLB is a single self-contained file (geometry + textures). OBJ works but
          may need its .mtl for materials. IFC/Revit isn't supported yet.
        </div>
      </div>

      <div style={styles.footNote}>
        Uploads are design-stage references to brief the analysis — confirm any
        extracted room data before relying on it. PDFs are rasterised before parsing.
      </div>
    </div>
  );
}

// ---- styles ---------------------------------------------------------------
const styles = {
  card: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
    padding: 12, color: T.text, fontSize: 13, display: "flex", flexDirection: "column", gap: 12,
  },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  cardTitle: { fontSize: 13, fontWeight: 600, letterSpacing: 0.2 },
  cardMeta: { fontSize: 11, color: T.textDim },
  section: { display: "flex", flexDirection: "column", gap: 8 },
  sectionLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: T.textDim },
  slot: { display: "flex", flexDirection: "column", gap: 4 },
  slotHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  slotLabel: { fontSize: 12, color: T.text },
  drop: {
    border: `1px dashed ${T.border}`, borderRadius: 6, padding: 10, cursor: "pointer",
    transition: "border-color .12s, background .12s", minHeight: 44, display: "flex", alignItems: "center",
  },
  emptyRow: { display: "flex", flexDirection: "column", gap: 2 },
  emptyMain: { fontSize: 12, color: T.textDim },
  emptyHint: { fontSize: 10, color: T.textDim, opacity: 0.7 },
  filledRow: { display: "flex", alignItems: "center", gap: 10, width: "100%" },
  thumb: { width: 40, height: 40, objectFit: "cover", borderRadius: 4, border: `1px solid ${T.border}`, flexShrink: 0 },
  thumbFallback: {
    width: 40, height: 40, borderRadius: 4, border: `1px solid ${T.border}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10, fontWeight: 600, color: T.textDim, flexShrink: 0,
  },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: { fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  fileSub: { fontSize: 10, color: T.textDim, marginTop: 2 },
  addRow: { marginTop: 2 },
  select: {
    width: "100%", background: T.surfaceHi, color: T.text,
    border: `1px solid ${T.border}`, borderRadius: 6, padding: "7px 8px", fontSize: 12,
  },
  linkBtn: { background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 11, padding: 0 },
  note: { fontSize: 10, color: T.textDim, lineHeight: 1.4 },
  footNote: { fontSize: 10, color: T.textDim, lineHeight: 1.4, borderTop: `1px solid ${T.border}`, paddingTop: 8 },
};
